<script lang="ts" setup>
    import {
        ModelGuarantee,
        PER_MILLION_TOKENS,
        type ModelCatalogEntry,
    } from 'models'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { modelAvailabilityNote } from './modelCatalog'

    const props = defineProps<{
        entry?: ModelCatalogEntry
        /** Fallback for a model the catalog does not describe (an old id). */
        modelName?: string
        /** Drawn only in the dropdown: the closed field has no room. */
        detailed?: boolean
        /**
         * The name on its own line and the details under it. A dropdown is as
         * wide as the field that opens it, and on one line the details, which
         * cannot shrink, squeezed the name to nothing: the one thing you pick
         * by was the only thing that gave way.
         */
        stacked?: boolean
    }>()

    const { t: $t, n: $n } = useI18n({ useScope: 'global' })

    // `||` and not `??`: an entry with an empty label would draw an empty
    // row, where the id is at least something to read.
    const label = computed(() => props.entry?.label || props.modelName || '')
    const icon = computed(() => props.entry?.brandIcon ?? 'ri:cpu-line')
    const iconPrefix = computed(() =>
        icon.value.startsWith('custom:') ? 'custom' : undefined,
    )
    const iconName = computed(() => icon.value.replace(/^custom:/, ''))

    /** Retired, or switched off for this organization: said on the row. */
    const note = computed(() => modelAvailabilityNote(props.entry))

    /**
     * Input and output per million tokens, which is the unit every provider
     * publishes and nobody writes per token.
     */
    const price = computed(() => {
        const pricing = props.entry?.pricing
        if (!pricing) {
            return undefined
        }
        // The named format, which carries `narrowSymbol`: inline options
        // printed the ISO code, so a row read "0,05 USD / 0,40 USD".
        const money = (perToken: number) =>
            $n(perToken * PER_MILLION_TOKENS, 'USD')
        return `${money(pricing.inputUsdPerToken)} / ${money(pricing.outputUsdPerToken)}`
    })

    const contextWindow = computed(() => {
        const window = props.entry?.contextWindow
        if (!window) {
            return undefined
        }
        // 'integer' groups the thousands: a bare $n() formats nothing, so a
        // 1050k window printed as "1050k" instead of "1.050k".
        return $t('label.contextTokens', {
            count: $n(Math.round(window / 1000), 'integer'),
        })
    })

    /**
     * The two privacy guarantees as three-state dots. `some` is the common
     * case, not an anomaly: the gateway serves the model through several
     * upstream providers and routes to the ones that honour it.
     */
    const guarantees = computed(() => {
        const entry = props.entry
        if (!entry || entry.source === 'byok') {
            return []
        }
        const suffix = (value: ModelGuarantee) =>
            value === ModelGuarantee.All
                ? 'All'
                : value === ModelGuarantee.Some
                  ? 'Some'
                  : 'None'
        return (
            [
                ['zeroDataRetention', entry.zdr],
                ['disallowPromptTraining', entry.noTraining],
            ] as const
        )
            .filter(([, value]) => value !== ModelGuarantee.Unknown)
            .map(([key, value]) => ({
                key,
                value,
                title: $t(`hint.guarantee${suffix(value)}`, {
                    name: $t(`label.${key}`),
                }),
            }))
    })

    /**
     * The second line of a stacked row. The guarantees stay out of it: the
     * picker already hides the models that cannot honour a flag the agent has
     * switched on, so in that list they are all but redundant, and two dots
     * with nothing on screen to decode them explained nothing anyway. They
     * live where the catalog is read, the Models page.
     */
    const details = computed(() =>
        [contextWindow.value, price.value].filter(Boolean).join(' · '),
    )
</script>

<template>
    <div v-if="stacked" class="flex items-center gap-8 min-w-0 w-full">
        <VvIcon
            :name="iconName"
            :prefix="iconPrefix"
            class="shrink-0 w-16 h-16"
            :title="entry?.brand" />
        <div class="flex flex-col gap-2 min-w-0">
            <span class="flex items-center gap-8 min-w-0 leading-tight">
                <span class="truncate">{{ label }}</span>
                <span
                    v-if="note"
                    class="text-12 text-word-3 shrink-0"
                    :title="$t(note.hint)">
                    {{ $t(note.label) }}
                </span>
            </span>
            <span
                v-if="details"
                class="text-12 text-word-3 truncate leading-tight">
                {{ details }}
            </span>
        </div>
    </div>
    <div v-else class="flex items-center gap-8 min-w-0 w-full">
        <VvIcon
            :name="iconName"
            :prefix="iconPrefix"
            class="shrink-0 w-16 h-16"
            :title="entry?.brand" />
        <span class="truncate">{{ label }}</span>
        <span
            v-if="note"
            class="text-12 text-word-3 shrink-0"
            :title="$t(note.hint)">
            {{ $t(note.label) }}
        </span>
        <template v-if="detailed">
            <span class="ml-auto flex items-center gap-8 shrink-0">
                <span
                    v-if="contextWindow"
                    class="text-12 text-word-3 none sm:inline">
                    {{ contextWindow }}
                </span>
                <span v-if="price" class="text-12 text-word-3">
                    {{ price }}
                </span>
                <span
                    v-for="guarantee in guarantees"
                    :key="guarantee.key"
                    class="pk-model-option__dot"
                    :class="`pk-model-option__dot--${guarantee.value}`"
                    :title="guarantee.title" />
            </span>
        </template>
    </div>
</template>

<style lang="scss">
    // Three states and no colour: a guarantee is not a status to act on, and
    // the palette in a picker belongs to the choice being made (DESIGN.md).
    .pk-model-option__dot {
        width: var(--spacing-8);
        height: var(--spacing-8);
        border-radius: var(--rounded-full);
        border: var(--spacing-px) solid var(--color-word-3);
        flex-shrink: 0;

        &--all {
            background-color: var(--color-word-2);
            border-color: var(--color-word-2);
        }

        &--some {
            // Half filled: the guarantee holds on some providers only.
            background: linear-gradient(
                to right,
                var(--color-word-2) 50%,
                transparent 50%
            );
        }

        &--none {
            background-color: transparent;
        }
    }
</style>
